import { useState, useCallback, useEffect, useRef } from "react";
import { getFriendlyErrorMessage } from "../utils/errorMapper";

const API_BASE = import.meta.env.VITE_WPP_API_BASE as string;
const SECRET_KEY = import.meta.env.VITE_WPP_SECRET_KEY as string;
const STORAGE_KEY = "wpp_session_state";

export type SessionStatus =
  | "idle"
  | "generating"
  | "token_ready"
  | "starting"
  | "qr_ready"
  | "connected"
  | "error";

interface StoredState {
  wppToken: string | null;
  status: SessionStatus;
  version: string | null;
}

interface GenerateTokenResponse {
  status: string;
  session: string;
  token: string;
  full: string;
}

interface StartSessionResponse {
  status: string;
  qrcode: string | null;
  version: string;
  session: string;
}

interface CheckConnectionResponse {
  status: boolean;
  message: string;
}

interface StatusSessionResponse {
  status: string;
  qrcode: string | null;
  version: string;
}

function loadStoredState(sessionName: string): StoredState | null {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}_${sessionName}`);
    if (!raw) return null;
    return JSON.parse(raw) as StoredState;
  } catch {
    return null;
  }
}

function saveStoredState(sessionName: string, state: StoredState): void {
  localStorage.setItem(`${STORAGE_KEY}_${sessionName}`, JSON.stringify(state));
}

function clearStoredState(sessionName: string): void {
  localStorage.removeItem(`${STORAGE_KEY}_${sessionName}`);
}

export function useWhatsAppSession(sessionName: string) {
  const [wppToken, setWppToken] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [status, setStatus] = useState<SessionStatus>("idle");
  const [error, setErrorState] = useState<string | null>(null);
  const setError = useCallback((err: string | null) => {
    setErrorState(err ? getFriendlyErrorMessage(err) : null);
  }, []);
  const [version, setVersion] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  useEffect(() => {
    if (!sessionName) return;
    const stored = loadStoredState(sessionName);
    if (stored) {
      if (stored.wppToken) setWppToken(stored.wppToken);
      if (stored.version) setVersion(stored.version);
      if (stored.status === "connected") {
        setStatus("connected");
      } else if (stored.status === "token_ready") {
        setStatus("token_ready");
      }
    }
  }, [sessionName]);

  useEffect(() => {
    if (status === "idle") setVersion(null);
  }, [status]);

  const generateToken = useCallback(async (): Promise<boolean> => {
    if (!sessionName) return false;
    setError(null);
    setStatus("generating");

    try {
      const url = `${API_BASE}/${sessionName}/${SECRET_KEY}/generate-token`;
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Unauthorized. Check your SECRET_KEY.");
        }
        throw new Error(`Server error (${res.status})`);
      }

      const json: GenerateTokenResponse = await res.json();

      if (json.status === "success" && json.token) {
        setWppToken(json.token);
        setStatus("token_ready");
        saveStoredState(sessionName, { wppToken: json.token, status: "token_ready", version: null });
        return true;
      }

      throw new Error("Invalid token response from server");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to generate token";
      setError(msg);
      setStatus("error");
      return false;
    }
  }, [sessionName]);

  const startSession = useCallback(async (): Promise<boolean> => {
    if (!sessionName || !wppToken) return false;
    setError(null);
    setQrCode(null);
    setStatus("starting");

    try {
      const url = `${API_BASE}/${sessionName}/start-session`;
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${wppToken}`,
        },
        body: JSON.stringify({
          webhook: "https://wpp.debugin.com/ai/webhook",
          waitQrCode: true,
        }),
      });

      if (!res.ok) {
        if (res.status === 401) throw new Error("Unauthorized. Token may be invalid.");
        throw new Error(`Server error (${res.status})`);
      }

      const json: StartSessionResponse = await res.json();

      if (json.status === "QRCODE" || json.status === "STARTING" || json.status === "INITIALIZING" || json.status === "NOT_CONNECTED") {
        if (json.qrcode) {
          setQrCode(json.qrcode);
          setVersion(json.version);
          setStatus("qr_ready");
          saveStoredState(sessionName, { wppToken, status: "qr_ready", version: json.version });
        } else {
          setStatus("starting");
          saveStoredState(sessionName, { wppToken, status: "starting", version: json.version });
        }
        return true;
      }

      if (json.status === "CONNECTED") {
        let isRealConnected = false;
        try {
          const connRes = await fetch(`${API_BASE}/${sessionName}/check-connection-session`, {
            headers: { Authorization: `Bearer ${wppToken}` },
          });
          if (connRes.ok) {
            const connJson = await connRes.json();
            if (connJson.status === true) {
              isRealConnected = true;
            }
          }
        } catch {
          // ignore
        }

        if (isRealConnected) {
          setVersion(json.version);
          setStatus("connected");
          saveStoredState(sessionName, { wppToken, status: "connected", version: json.version });
          return true;
        } else {
          // Zombie session detected on server! Kill it to force a fresh QR Code
          try {
            await fetch(`${API_BASE}/${sessionName}/logout-session`, {
              method: "POST",
              headers: { Authorization: `Bearer ${wppToken}` },
            });
            await fetch(`${API_BASE}/${sessionName}/close-session`, {
              method: "POST",
              headers: { Authorization: `Bearer ${wppToken}` },
            });
          } catch {
            // ignore
          }

          // Restart session to generate fresh QR code
          const retryRes = await fetch(url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${wppToken}`,
            },
            body: JSON.stringify({
              webhook: "https://wpp.debugin.com/ai/webhook",
              waitQrCode: true,
            }),
          });

          if (retryRes.ok) {
            const retryJson: StartSessionResponse = await retryRes.json();
            if (retryJson.qrcode) {
              setQrCode(retryJson.qrcode);
              setVersion(retryJson.version);
              setStatus("qr_ready");
              saveStoredState(sessionName, { wppToken, status: "qr_ready", version: retryJson.version });
              return true;
            }
          }

          setStatus("starting");
          return true;
        }
      }

      throw new Error(`Unexpected status: ${json.status}`);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to start session";
      setError(msg);
      setStatus("idle");
      setWppToken(null);
      setQrCode(null);
      clearStoredState(sessionName);
      return false;
    }
  }, [sessionName, wppToken]);

  const checkConnection = useCallback(async (): Promise<boolean> => {
    if (!sessionName || !wppToken) return false;
    setIsChecking(true);

    try {
      const url = `${API_BASE}/${sessionName}/check-connection-session`;
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${wppToken}`,
        },
      });

      if (!res.ok) {
        setStatus("idle");
        setWppToken(null);
        setQrCode(null);
        clearStoredState(sessionName);
        return false;
      }

      const json: CheckConnectionResponse = await res.json();

      if (json.status === true) {
        // Secondary verification with status-session
        try {
          const statusRes = await fetch(`${API_BASE}/${sessionName}/status-session`, {
            headers: { Authorization: `Bearer ${wppToken}` },
          });
          if (statusRes.ok) {
            const statusJson = await statusRes.json();
            if (statusJson.status !== "CONNECTED") {
              setStatus("idle");
              setWppToken(null);
              setQrCode(null);
              clearStoredState(sessionName);
              return false;
            }
          }
        } catch {
          // ignore secondary check error
        }

        setStatus("connected");
        setQrCode(null);
        saveStoredState(sessionName, { wppToken, status: "connected", version });
        return true;
      }

      setStatus("idle");
      setWppToken(null);
      setQrCode(null);
      clearStoredState(sessionName);
      return false;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to check connection";
      setError(msg);
      setStatus("idle");
      setWppToken(null);
      setQrCode(null);
      clearStoredState(sessionName);
      return false;
    } finally {
      setIsChecking(false);
    }
  }, [sessionName, wppToken, version]);

  // Automatically verify actual connection state on server ONCE on mount if stored as connected
  const initialCheckedRef = useRef(false);
  useEffect(() => {
    if (!initialCheckedRef.current && sessionName && wppToken && status === "connected") {
      initialCheckedRef.current = true;
      checkConnection();
    }
  }, [sessionName, wppToken, status, checkConnection]);

  const checkStatus = useCallback(async (): Promise<string | null> => {
    if (!sessionName || !wppToken) return null;
    setIsChecking(true);

    try {
      const url = `${API_BASE}/${sessionName}/status-session`;
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${wppToken}`,
        },
      });

      if (!res.ok) {
        setStatus("idle");
        setWppToken(null);
        setQrCode(null);
        clearStoredState(sessionName);
        throw new Error(`Server error (${res.status})`);
      }

      const json: StatusSessionResponse = await res.json();

      if (json.status === "CONNECTED") {
        let isRealConnected = false;
        try {
          const connRes = await fetch(`${API_BASE}/${sessionName}/check-connection-session`, {
            headers: { Authorization: `Bearer ${wppToken}` },
          });
          if (connRes.ok) {
            const connJson = await connRes.json();
            if (connJson.status === true) {
              isRealConnected = true;
            }
          }
        } catch {
          // ignore error
        }

        if (isRealConnected) {
          setStatus("connected");
          setQrCode(null);
          setVersion(json.version);
          saveStoredState(sessionName, { wppToken, status: "connected", version: json.version });
          return "CONNECTED";
        } else {
          setStatus("idle");
          setWppToken(null);
          setQrCode(null);
          clearStoredState(sessionName);
          return "NOT_CONNECTED";
        }
      }

      // Extract QR code from status-session or fall back to /qr-code endpoint
      let extractedQr: string | null =
        json.qrcode ||
        (json as any).response?.qrcode ||
        (json as any).result?.qrcode ||
        (json as any).qr ||
        null;

      if (!extractedQr && (json.status === "STARTING" || json.status === "INITIALIZING" || json.status === "NOT_CONNECTED" || json.status === "QRCODE")) {
        try {
          const qrRes = await fetch(`${API_BASE}/${sessionName}/qrcode-session`, {
            headers: { Authorization: `Bearer ${wppToken}` },
          });
          if (qrRes.ok) {
            const contentType = qrRes.headers.get("content-type") || "";
            if (contentType.includes("image")) {
              const blob = await qrRes.blob();
              extractedQr = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result as string);
                reader.readAsDataURL(blob);
              });
            } else {
              const qrJson = await qrRes.json();
              extractedQr = qrJson?.qrcode || qrJson?.response?.qrcode || qrJson?.result || (typeof qrJson === "string" ? qrJson : null);
            }
          }
        } catch {
          // ignore fallback error
        }
      }

      if (extractedQr && typeof extractedQr === "string") {
        const finalQr = extractedQr.startsWith("data:") || extractedQr.startsWith("http")
          ? extractedQr
          : `data:image/png;base64,${extractedQr}`;
        setQrCode(finalQr);
        setStatus("qr_ready");
        saveStoredState(sessionName, { wppToken, status: "qr_ready", version: json.version });
        return "QRCODE";
      }

      if (
        json.status === "STARTING" ||
        json.status === "INITIALIZING" ||
        json.status === "NOT_CONNECTED" ||
        json.status === "qr_ready"
      ) {
        setStatus("starting");
        return json.status;
      }

      if (
        json.status === "CLOSED" ||
        json.status === "DISCONNECTED" ||
        json.status === "LOGOUT" ||
        json.status === "unauthorized"
      ) {
        setStatus("idle");
        setWppToken(null);
        setQrCode(null);
        clearStoredState(sessionName);
        return json.status;
      }

      setStatus("starting");
      return json.status;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to check status";
      setError(msg);
      setStatus("idle");
      setWppToken(null);
      setQrCode(null);
      clearStoredState(sessionName);
      return null;
    } finally {
      setIsChecking(false);
    }
  }, [sessionName, wppToken, version]);

  const disconnect = useCallback(async () => {
    if (sessionName && wppToken) {
      try {
        await fetch(`${API_BASE}/${sessionName}/logout-session`, {
          method: "POST",
          headers: { Authorization: `Bearer ${wppToken}` },
        });
        await fetch(`${API_BASE}/${sessionName}/close-session`, {
          method: "POST",
          headers: { Authorization: `Bearer ${wppToken}` },
        });
      } catch {
        // ignore
      }
    }
    setWppToken(null);
    setQrCode(null);
    setStatus("idle");
    setError(null);
    setVersion(null);
    if (sessionName) clearStoredState(sessionName);
  }, [sessionName, wppToken]);

  return {
    wppToken,
    qrCode,
    status,
    error,
    version,
    isChecking,
    generateToken,
    startSession,
    checkConnection,
    checkStatus,
    disconnect,
  };
}